import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  CheckCircle, 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Phone, 
  Mail,
  ArrowRight,
  Star
} from 'lucide-react';
import { paymentApi } from '../../services/paymentService';

const ClientBookingSuccess = () => {
  const location = useLocation();
  const bookingData = location.state?.bookingData || {};
  const serviceData = location.state?.serviceData || {};
  const vendorData = location.state?.vendorData || {};

  // Fetch payments for this booking to get actual deposit amount
  const { data: paymentsResponse } = useQuery({
    queryKey: ['payments', bookingData.id],
    queryFn: () => paymentApi.getPaymentByBooking(bookingData.id),
    enabled: !!bookingData.id,
    select: (response) => response?.data || []
  });

  const payments = paymentsResponse || [];
  const completedPayments = payments.filter(p => p.status === 'completed');
  
  // Get actual deposit amount from payments table
  const depositPayment = completedPayments.find(p => p.payment_type === 'deposit');
  const depositAmount = depositPayment ? parseFloat(depositPayment.amount || 0) : 0;
  
  // Check if full payment was made
  const fullPayment = completedPayments.find(p => p.payment_type === 'full_payment');
  const hasFullPayment = !!fullPayment;
  const hasDepositOnly = depositAmount > 0 && !hasFullPayment;

  // If no booking data is available, show a fallback message
  if (!bookingData.id) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-gray-300 rounded-full mb-6">
            <CheckCircle className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-black mb-4">
            No Booking Found
          </h1>
          <p className="text-base text-gray-600 mb-8">
            It looks like you don't have a valid booking to view.
          </p>
          <Link to="/">
            <button className="px-4 py-2 bg-black text-white rounded-md text-sm font-medium hover:bg-gray-900 transition-colors">
              Go Home
            </button>
          </Link>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-white">
      <div className="page-container py-12">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-black rounded-full mb-6">
            <CheckCircle className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-3xl font-bold text-black mb-4">
            Booking Confirmed
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Your booking has been successfully created. You'll receive a confirmation email shortly with all the details.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Booking Summary Card */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-black mb-6">Booking Summary</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Service Details */}
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Calendar className="w-4 h-4 text-black mr-3 mt-1" />
                    <div>
                      <h3 className="text-sm font-semibold text-black mb-1">Event Date</h3>
                      <p className="text-sm text-gray-600">
                        {bookingData.eventDate ? new Date(bookingData.eventDate).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : 'Not specified'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Clock className="w-4 h-4 text-black mr-3 mt-1" />
                    <div>
                      <h3 className="text-sm font-semibold text-black mb-1">Event Time</h3>
                      <p className="text-sm text-gray-600">
                        {bookingData.eventTime || 'Not specified'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 text-black mr-3 mt-1" />
                    <div>
                      <h3 className="text-sm font-semibold text-black mb-1">Event Location</h3>
                      <p className="text-sm text-gray-600">
                        {bookingData.location || 'Not specified'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Package Details */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-black mb-2">Service Package</h3>
                    <div className="border-2 border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-black mb-1">
                        {bookingData.selectedPackage?.name || 'Package Name'}
                      </h4>
                      <p className="text-sm text-gray-600 mb-3">
                        {bookingData.selectedPackage?.description || 'Package description'}
                      </p>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                        <span className="text-base font-bold text-black">
                          ${Number(bookingData.selectedPackage?.price || 0).toFixed(2)}
                        </span>
                        <span className="text-xs text-gray-600">
                          {bookingData.duration} hours
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-black mb-2">Total Amount</h3>
                    <div className="border-2 border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                        <span className="text-sm font-semibold text-black">Total</span>
                        <span className="text-xl font-bold text-black">
                          ${Number(bookingData.totalAmount || bookingData.subtotal_amount + bookingData.platform_fee || 0).toFixed(2)}
                        </span>
                      </div>
                      {hasFullPayment && (
                        <div className="flex justify-between items-center mt-2 text-xs text-gray-600">
                          <span>Full Payment Paid</span>
                          <span>${parseFloat(fullPayment.amount || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {hasDepositOnly && (
                        <div className="flex justify-between items-center mt-2 text-xs text-gray-600">
                          <span>Deposit Paid</span>
                          <span>${depositAmount.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="border-2 border-gray-200 rounded-lg p-6 mb-6">
              <h3 className="text-base font-semibold text-black mb-4">What's Next?</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="w-5 h-5 border-2 border-black rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-xs font-semibold text-black">1</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-black">Confirmation Email</p>
                    <p className="text-xs text-gray-600">Check your email for booking details and next steps</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-5 h-5 border-2 border-black rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-xs font-semibold text-black">2</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-black">Vendor Contact</p>
                    <p className="text-xs text-gray-600">The vendor will contact you within 24 hours to confirm details</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-5 h-5 border-2 border-black rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-xs font-semibold text-black">3</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-black">Event Preparation</p>
                    <p className="text-xs text-gray-600">Prepare for your event and enjoy the experience!</p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Vendor Info */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
              <h3 className="text-base font-semibold text-black mb-4">Vendor Information</h3>
              
              <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
                <div className="w-10 h-10 border-2 border-black rounded-lg flex items-center justify-center mr-3">
                  <User className="w-5 h-5 text-black" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-black">
                    {vendorData.business_name || 'Vendor Name'}
                  </h4>
                  <div className="flex items-center text-xs text-gray-600">
                    <Star className="w-3 h-3 text-black fill-current mr-1" />
                    {Number(serviceData.average_rating || 0).toFixed(1)} ({serviceData.total_reviews || 0} reviews)
                  </div>
                </div>
              </div>

              <div className="space-y-3 text-xs">
                <div className="flex items-center">
                  <MapPin className="w-3 h-3 text-black mr-2" />
                  <span className="text-gray-600">{vendorData.city}, {vendorData.state}</span>
                </div>
                <div className="flex items-center">
                  <Phone className="w-3 h-3 text-black mr-2" />
                  <span className="text-gray-600">Contact details will be shared after confirmation</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-3 h-3 text-black mr-2" />
                  <span className="text-gray-600">Email confirmation sent</span>
                </div>
              </div>
            </div>

            {/* Service Details */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <h3 className="text-base font-semibold text-black mb-4">Service Details</h3>
              
              <div className="space-y-3">
                <div>
                  <h4 className="text-xs font-semibold text-black mb-1">Service</h4>
                  <p className="text-xs text-gray-600">{serviceData.service_name || 'Service Name'}</p>
                </div>
                
                <div>
                  <h4 className="text-xs font-semibold text-black mb-1">Category</h4>
                  <p className="text-xs text-gray-600">{serviceData.category_name || 'Category'}</p>
                </div>
                
                <div>
                  <h4 className="text-xs font-semibold text-black mb-1">Duration</h4>
                  <p className="text-xs text-gray-600">{bookingData.duration} hours</p>
                </div>
                
                <div>
                  <h4 className="text-xs font-semibold text-black mb-1">Guest Count</h4>
                  <p className="text-xs text-gray-600">{bookingData.guestCount || 'Not specified'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="mt-12 text-center">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/bookings">
              <button className="inline-flex items-center justify-center px-4 py-2 border-2 border-black text-black rounded-md text-sm font-medium hover:bg-gray-100 transition-colors">
                <Calendar className="w-4 h-4 mr-2" />
                View My Bookings
              </button>
            </Link>
            <Link to="/browse">
              <button className="inline-flex items-center justify-center px-4 py-2 bg-black text-white rounded-md text-sm font-medium hover:bg-gray-900 transition-colors">
                Browse More Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientBookingSuccess;
